import { apiGet, apiPost, apiPut, apiPatch } from './api';

export type AdminUserRow = {
  _id: string;
  email: string;
  nickname?: string;
  role?: string;
  isTeacher?: boolean;
  status?: string;
  teacherId?: string | null;
  studentIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  lastConnected?: string | null;
  firstName?: string;
  lastName?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  avatarUrl?: string;
  subscriptionState?: string;
  teacherLabel?: string | null;
  preferredMusicStyles?: string[];
};

export type UserDetail = {
  userAuth?: {
    _id: string;
    email: string;
    nickname?: string;
    role?: string;
    isTeacher?: boolean;
    status?: string;
    teacherId?: string | null;
    studentIds?: string[];
    teacherAllowedFileLevels?: number[];
    createdAt?: string;
    updatedAt?: string;
    teacher?: {
      teacherId: string;
      teacherNickname?: string;
      teacherFirstName?: string;
      teacherLastName?: string;
    } | null;
    students?: Array<{
      studentId: string;
      studentNickname?: string;
      studentFirstName?: string;
      studentLastName?: string;
    }>;
  };
  userInfos?: {
    firstName?: string;
    lastName?: string;
    city?: string;
    postalCode?: string;
    phone?: string;
    address1?: string;
    address2?: string;
    birthdate?: string;
    newsletterConsent?: boolean;
  };
  userAvatar?: {
    avatarUrl?: string;
  };
};

export async function getAllUsers(): Promise<{ success: boolean; data?: { users: AdminUserRow[]; pagination?: { page: number; limit: number; total: number; hasMore: boolean } } }> {
  return apiGet('/api/v1/user/all?limit=200');
}

export async function getUserById(userId: string): Promise<{ success: boolean; user: UserDetail }> {
  return apiGet(`/api/v1/user/${userId}`);
}

export async function updateUserProfile(
  userId: string,
  body: Record<string, unknown>
): Promise<{ success: boolean; user?: unknown }> {
  return apiPut(`/api/v1/user/${userId}`, body);
}

export async function updateContactInfos(
  userId: string,
  body: { phone?: string; address1?: string; address2?: string }
): Promise<{ success: boolean }> {
  return apiPut(`/api/v1/user/${userId}/contactInfos`, body);
}

export async function updateUserAccount(
  userId: string,
  body: { role?: string; status?: string; nickname?: string; teacherAllowedFileLevels?: number[] }
): Promise<{ success: boolean; user?: AdminUserRow }> {
  return apiPatch(`/api/v1/admin/users/${userId}/account`, body);
}

export async function resetUserPassword(
  userId: string,
  password: string
): Promise<{ success: boolean; message?: string }> {
  return apiPost(`/api/v1/admin/users/${userId}/reset-password`, { password });
}

export async function assignTeacher(
  studentId: string,
  teacherId: string
): Promise<{ success: boolean }> {
  return apiPost(`/api/v1/user/${studentId}/assign-teacher`, { teacherId });
}

export async function deleteUserAndData(
  userId: string,
  confirmEmail: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(
    `${(import.meta.env.VITE_API_BASE_URL as string) || ''}/api/v1/admin/users/${userId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmEmail }),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}

export async function unassignTeacherFromStudent(studentId: string): Promise<{ success: boolean }> {
  const res = await fetch(
    `${(import.meta.env.VITE_API_BASE_URL as string) || ''}/api/v1/user/${studentId}/assign-teacher`,
    { method: 'DELETE', credentials: 'include' }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}
